import { auth } from './firebase';
import { logger } from './logger';
import type {
  Child,
  SleepEvent,
  SleepGoal,
  UserProfile,
  NewSleepEvent,
  GenerateSleepGoalsInput,
  AnalyzeSleepPatternsInput,
  GenerateSuggestedPromptsInput
} from './types';
import { Timestamp } from 'firebase/firestore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://us-central1-lullabai-nn6qs.cloudfunctions.net/api';

export interface BootstrapData {
  isVip: boolean;
  profile: UserProfile | null;
  children: Child[];
  selectedChildId: string | null;
  sleepEvents: SleepEvent[];
  goals: SleepGoal[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  cancelled?: boolean;
}

class ApiClient {
  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  }

  private transformSleepEvent(event: any): SleepEvent {
    let transformedTime = null;

    if (event.time) {
      try {
        // Handle different time formats that might come from the API
        let date: Date;

        if (event.time instanceof Date) {
          date = event.time;
        } else if (typeof event.time === 'string' || typeof event.time === 'number') {
          date = new Date(event.time);
        } else if (event.time && typeof event.time === 'object' && typeof event.time.toDate === 'function') {
          // This is already a Firestore Timestamp
          date = event.time.toDate();
        } else if (event.time && typeof event.time === 'object' && (event.time.seconds !== undefined || event.time._seconds !== undefined)) {
          // Serialized Firestore Timestamp (from JSON: client uses seconds, admin uses _seconds)
          const seconds = event.time.seconds ?? event.time._seconds;
          const nanoseconds = event.time.nanoseconds ?? event.time._nanoseconds ?? 0;
          date = new Date(seconds * 1000 + nanoseconds / 1000000);
        } else {
          console.warn('Unexpected time format in event:', event.id, event.type, event.time);
          return { ...event, time: null };
        }

        // Check if the date is valid
        if (!isNaN(date.getTime())) {
          transformedTime = Timestamp.fromDate(date);
        } else {
          console.warn('Invalid date in event:', event.id, event.type, event.time);
        }
      } catch (error) {
        console.warn('Error transforming timestamp for event:', event, 'Original time value:', event.time, error);
      }
    } else {
      console.warn('Event has no time field:', event);
    }

    return {
      ...event,
      time: transformedTime
    };
  }

  private transformSleepEvents(events: any[]): SleepEvent[] {
    return events.map(event => this.transformSleepEvent(event));
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs: number = 30000, // Default 30 second timeout
    abortSignal?: AbortSignal
  ): Promise<ApiResponse<T>> {
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let didTimeout = false;
    let wasExternallyAborted = false;

    if (abortSignal) {
      if (abortSignal.aborted) {
        wasExternallyAborted = true;
        const reason = (abortSignal as any).reason;
        if (typeof reason !== 'undefined') {
          controller.abort(reason);
        } else {
          controller.abort();
        }
      } else {
        abortSignal.addEventListener(
          'abort',
          () => {
            wasExternallyAborted = true;
            const reason = (abortSignal as any).reason;
            if (typeof reason !== 'undefined') {
              controller.abort(reason);
            } else {
              controller.abort();
            }
          },
          { once: true }
        );
      }
    }

    if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
      timeoutId = setTimeout(() => {
        didTimeout = true;
        if (typeof DOMException !== 'undefined') {
          controller.abort(new DOMException('Request timed out', 'TimeoutError'));
        } else {
          controller.abort('timeout');
        }
      }, timeoutMs);
    }

    try {
      const token = await this.getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
        signal: controller.signal,
      });

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // If we can't parse JSON, use the status text or default message
          errorMessage = response.statusText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);

      // Enhanced error detection
      let errorMessage = 'Network error';

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          if (didTimeout) {
            errorMessage = 'Request timed out. Please check your internet connection and try again.';
          } else if (wasExternallyAborted) {
            return {
              success: false,
              error: 'Request was cancelled.',
              cancelled: true,
            };
          } else {
            errorMessage = 'Request was aborted.';
          }
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timed out. Please check your internet connection and try again.';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
        } else if (error.message.includes('offline') || (typeof navigator !== 'undefined' && !navigator.onLine)) {
          errorMessage = 'You appear to be offline. Please check your internet connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }



      // Log to Firestore only if it wasn't a cancellation
      if (!wasExternallyAborted) {
        logger.error(`API request failed: ${endpoint}`, {
          endpoint,
          error: error instanceof Error ? error.message : String(error),
          name: error instanceof Error ? error.name : 'UnknownError',
          finalMessage: errorMessage
        });
      }

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  // ===== BOOTSTRAP API (SINGLE ROUND TRIP) =====

  async getBootstrapData(childId?: string): Promise<ApiResponse<BootstrapData>> {
    const endpoint = childId ? `/api/bootstrap?childId=${encodeURIComponent(childId)}` : '/api/bootstrap';
    const response = await this.makeRequest<any>(endpoint);

    if (response.success && response.data) {
      return {
        ...response,
        data: {
          ...response.data,
          sleepEvents: this.transformSleepEvents(response.data.sleepEvents || [])
        }
      };
    }

    // Fallback: If /api/bootstrap is unavailable or fails, fetch via parallel requests
    try {
      const [vipRes, profileRes, childrenRes] = await Promise.all([
        this.getVipStatus(),
        this.getUserProfile(),
        this.getChildren(),
      ]);

      const children = childrenRes.success && childrenRes.data ? childrenRes.data : [];
      const activeChildId = childId && children.some(c => c.id === childId)
        ? childId
        : (children.length > 0 ? children[0].id : null);

      let sleepEvents: SleepEvent[] = [];
      let goals: SleepGoal[] = [];

      if (activeChildId) {
        const [eventsRes, goalsRes] = await Promise.all([
          this.getSleepEvents(activeChildId),
          this.getGoals(activeChildId)
        ]);
        if (eventsRes.success && eventsRes.data) sleepEvents = eventsRes.data;
        if (goalsRes.success && goalsRes.data) goals = goalsRes.data;
      }

      return {
        success: true,
        data: {
          isVip: vipRes.success && vipRes.data?.isVip ? true : false,
          profile: profileRes.success ? (profileRes.data || null) : null,
          children,
          selectedChildId: activeChildId,
          sleepEvents,
          goals
        }
      };
    } catch {
      return response;
    }
  }

  // ===== CHILDREN API =====

  async getChildren(): Promise<ApiResponse<Child[]>> {
    return this.makeRequest<Child[]>('/api/children');
  }

  async createChild(childData: Omit<Child, 'id' | 'userId'>): Promise<ApiResponse<Child>> {
    return this.makeRequest<Child>('/api/children', {
      method: 'POST',
      body: JSON.stringify(childData),
    });
  }

  async updateChild(child: Child): Promise<ApiResponse<Child>> {
    return this.makeRequest<Child>('/api/children', {
      method: 'POST',
      body: JSON.stringify(child),
    });
  }

  async deleteChild(childId: string): Promise<ApiResponse<{ message: string }>> {
    return this.makeRequest<{ message: string }>(`/api/children/${childId}`, {
      method: 'DELETE',
    });
  }

  // ===== SLEEP EVENTS API =====

  async getSleepEvents(childId: string): Promise<ApiResponse<SleepEvent[]>> {
    const response = await this.makeRequest<any[]>(`/api/sleep-events/${childId}`);

    if (response.success && response.data) {
      return {
        ...response,
        data: this.transformSleepEvents(response.data)
      };
    }

    return response;
  }

  async createSleepEvent(eventData: Omit<NewSleepEvent, 'childId'> & { childId: string }): Promise<ApiResponse<SleepEvent>> {
    const response = await this.makeRequest<any>('/api/sleep-events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });

    if (response.success && response.data) {
      return {
        ...response,
        data: this.transformSleepEvent(response.data)
      };
    }

    return response;
  }

  async updateSleepEvent(eventId: string, eventData: Omit<NewSleepEvent, 'childId'>): Promise<ApiResponse<SleepEvent>> {
    const response = await this.makeRequest<any>(`/api/sleep-events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(eventData),
    });

    if (response.success && response.data) {
      return {
        ...response,
        data: this.transformSleepEvent(response.data)
      };
    }

    return response;
  }

  async deleteSleepEvent(eventId: string): Promise<ApiResponse<{ message: string }>> {
    return this.makeRequest<{ message: string }>(`/api/sleep-events/${eventId}`, {
      method: 'DELETE',
    });
  }

  // ===== GOALS API =====

  async getGoals(childId: string): Promise<ApiResponse<SleepGoal[]>> {
    return this.makeRequest<SleepGoal[]>(`/api/goals/${childId}`);
  }

  async createGoal(goalData: Omit<SleepGoal, 'id' | 'userId' | 'reminders'>): Promise<ApiResponse<SleepGoal>> {
    return this.makeRequest<SleepGoal>('/api/goals', {
      method: 'POST',
      body: JSON.stringify(goalData),
    });
  }

  async updateGoal(goal: SleepGoal): Promise<ApiResponse<SleepGoal>> {
    return this.makeRequest<SleepGoal>('/api/goals', {
      method: 'POST',
      body: JSON.stringify(goal),
    });
  }

  // ===== USER PROFILE API =====

  async getUserProfile(): Promise<ApiResponse<UserProfile | null>> {
    return this.makeRequest<UserProfile | null>('/api/user/profile');
  }

  async createUserProfile(profileData: Omit<UserProfile, 'id'>): Promise<ApiResponse<UserProfile>> {
    return this.makeRequest<UserProfile>('/api/user/profile', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });
  }

  async updateUserProfile(profile: UserProfile): Promise<ApiResponse<UserProfile>> {
    return this.makeRequest<UserProfile>('/api/user/profile', {
      method: 'POST',
      body: JSON.stringify(profile),
    });
  }

  async getVipStatus(): Promise<ApiResponse<{ isVip: boolean }>> {
    return this.makeRequest<{ isVip: boolean }>('/api/user/vip-status');
  }

  // ===== AI COACHING API =====

  async analyzeSleepPatterns(
    data: AnalyzeSleepPatternsInput,
    signal?: AbortSignal
  ): Promise<ApiResponse<{ response: string, suggestedGoals?: SleepGoal }>> {
    return this.makeRequest<{ response: string, suggestedGoals?: SleepGoal }>('/api/analyze-sleep-patterns', {
      method: 'POST',
      body: JSON.stringify(data),
    }, 120000, signal);
  }

  async generateSleepGoals(input: GenerateSleepGoalsInput): Promise<ApiResponse<any>> {
    // Use 60 second timeout for gemini-2.5-flash which is faster
    return this.makeRequest('/api/generate-sleep-goals', {
      method: 'POST',
      body: JSON.stringify(input),
    }, 60000); // 1 minute
  }



  async generateSuggestedPrompts(input: GenerateSuggestedPromptsInput): Promise<ApiResponse<any>> {
    // Use 60 second timeout for gemini-2.5-flash which is faster
    return this.makeRequest('/api/generate-suggested-prompts', {
      method: 'POST',
      body: JSON.stringify(input),
    }, 60000); // 1 minute
  }

  // ===== CONVERSATION API =====

  async getConversation(childId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/conversations/${childId}`);
  }

  async saveConversation(childId: string, messages: any[]): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/conversations/${childId}`, {
      method: 'POST',
      body: JSON.stringify({ messages }),
    });
  }

  async deleteConversation(childId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/conversations/${childId}`, {
      method: 'DELETE',
    });
  }

  // ===== USAGE STATS API =====

  async getUsageStats(): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/usage-stats');
  }

  // ===== SUBSCRIPTION API =====

  async cancelSubscription(): Promise<ApiResponse<{ message: string }>> {
    return this.makeRequest<{ message: string }>('/api/cancel-subscription', {
      method: 'POST',
    });
  }

  async deleteAccount(): Promise<ApiResponse<{ message: string }>> {
    return this.makeRequest<{ message: string }>('/api/delete-account', {
      method: 'POST',
    });
  }

  async validateAndroidPurchase(purchaseToken: string, productId: string): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/validate-android-purchase', {
      method: 'POST',
      body: JSON.stringify({ purchaseToken, productId }),
    });
  }

  async validateIosReceipt(receipt: string, productId: string): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/validate-ios-receipt', {
      method: 'POST',
      body: JSON.stringify({ receipt, productId }),
    });
  }



  async getSubscriptionStatus(): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/subscription-status');
  }
}

export const apiClient = new ApiClient();

